import CategoryWell from 'owa-categories/lib/components/CategoryWell';
import type { MailListItemSingleLineViewProps } from './MailListItemComponentProps';
import onCategoryClicked from '../utils/onCategoryClicked';
import renderReactions from '../utils/renderReactions';
import isValidEmailAddress from '../utils/isValidEmailAddress';
import classnames from 'owa-classnames';
import loc from 'owa-localize';
import { observer } from 'owa-mobx-react';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getListViewColumnWidths } from 'owa-mail-layout';
import { isReadingPanePositionRight } from 'owa-mail-layout/lib/selectors/readingPanePosition';
import {
    onMailListItemClickHandler,
    useMailListItemContainerContext,
    showPersonaCheckbox,
} from 'owa-mail-list-item-shared';
import type { TableView, MailFolderTableQuery } from 'owa-mail-list-store';
import { type SortColumn } from 'owa-mail-list-store/lib/store/schema/SortBy';
import getListViewStore from 'owa-mail-list-store/lib/store/Store';
import getInstrumentationContextsFromTableView from 'owa-mail-list-store/lib/utils/getInstrumentationContextsFromTableView';
import {
    full,
    medium,
    compact,
    secondColumn,
    itemView as mailListitemStyles_itemView,
    conversationView as mailListitemStyles_conversationView,
    categoryWellSingleLine,
    categoryContainerSingleLine,
    previewDisplaySingleLine,
    reactionsContainer,
    mclRPRightThirdColumn,
} from 'owa-mail-listitem-styles/lib/scss/MailListItem.scss';
import {
    firstRowBase,
    singleLineViewContainer,
    content,
    subjectUnread,
    subject,
} from 'owa-mail-listitem-styles/lib/scss/ListViewItemCommonStyles.scss';
import {
    firstColumnWithHeaders,
    firstColumnNoTwistyWithHeaders,
    itemView,
    conversationView,
    standardizedFirstColumn,
    standardizedFirstColumnDep,
    standardizedSecondColumn,
    standardizedSecondColumnDep,
} from './MailListItemSingleLineView.scss';
import { useMessageListTextStyle } from 'owa-mail-listitem-styles/lib/utils/useMessageListTextStyle';
import MailListItemSelectionSource from 'owa-mail-store/lib/store/schema/MailListItemSelectionSource';
import shouldShowUnstackedReadingPane from 'owa-mail-organization-settings/lib/utils/shouldShowUnstackedReadingPane';
import type ReactListViewType from 'owa-service/lib/contract/ReactListViewType';
import { getMailboxInfo } from 'owa-mail-mailboxinfo';
import React from 'react';
import type { CSSProperties } from 'react';
import {
    MailListItemMarker,
    MailListItemFirstLineText,
    MailListItemIconBar,
    MailListItemLineText,
    MailListItemPreviewDisplay,
    MailListItemTwistyButton,
    MailListItemUnreadCount,
    PersonaCheckbox,
    MailListItemMeetingInfo,
    FolderTag,
    MailListItemRichPreviewWell,
    MailListItemTimestamp,
    MailListItemUnreadBar,
} from 'owa-mail-list-item-components';
import { SuggestedUnsubscribe } from 'owa-suggested-unsubscribe-peek';
import { getDensityModeCssClass, getDensityModeString, getIsDarkTheme } from 'owa-fabric-theme';
import {
    getConditionalFormattingStyles,
    ConditionalFormattingSurface,
} from 'owa-conditional-formatting';
import { senderEmailNotFoundLabel } from './MailListItem.locstring.json';
import { CopilotInboxPriorityIcon } from 'owa-mail-copilot-inbox-shared';
import {
    RECEIVED_COLUMN_LEFT_PADDING,
    MCL_RPRIGHT_LIST_ITEM_RIGHT_PADDING,
    MCL_LIST_ITEM_RIGHT_PADDING,
    DEFAULT_CF_PREVIEW_SENDER_COLUMN_WIDTH,
    DEFAULT_CF_PREVIEW_SUBJECT_COLUMN_WIDTH,
    DEFAULT_CF_PREVIEW_RECEIVED_COLUMN_WIDTH,
} from 'owa-mail-layout/lib/mailLayoutConstants';

const defaultExport: React.FunctionComponent<MailListItemSingleLineViewProps> = observer(
    function MailListItemSingleLineView(props: MailListItemSingleLineViewProps): JSX.Element {
        const {
            canArchive,
            canDelete,
            canPin,
            categories,
            conversationId,
            copilotInboxScore,
            effectiveMentioned,
            firstLineText,
            firstLineTooltipText,
            hasAttachment,
            hasSharepointLink,
            highlightTerms,
            importance,
            isComplete,
            isFirstLevelExpanded,
            isFlagged,
            isInArchiveFolder,
            isInNotesFolder,
            isInOutboxFolder,
            isInJunkFolder,
            isLoadingSecondLevelExpansion,
            isPinned,
            renderMinimum,
            isSearchTable,
            isSecondLevelExpanded,
            isSelected,
            isSnoozed,
            isTaggedForBigScreen,
            isUnauthenticatedSender,
            itemClassIcon,
            lastDeliveryTimestamp,
            lastSender,
            latestItemId,
            listViewType,
            mailboxGuids,
            parentFolderDisplayName,
            parentFolderId,
            returnTime,
            rowId,
            rowKey,
            secondLineText,
            secondLineTooltipText,
            shouldShowExternalLabel,
            shouldShowTwisty,
            shouldSuggestUnsubscribe,
            showAttachmentPreview,
            showCondensedView,
            showDraftsIndicator,
            showFolderTag,
            showPreviewText,
            smimeType,
            supportsFlagging,
            supportsHoverIcons,
            supportsMove,
            supportsPinning,
            tableViewId,
            thirdLineText,
            unreadCount,
            shouldShowRSVP,
            isReminderSet,
            isScheduledSend,
            bulkCategory,
            isCfPreview,
            cfPreviewMods,
            cfPreviewBackgroundColorStyle,
        } = props;

        const tableView: TableView = getListViewStore().tableViews.get(tableViewId) as TableView;
        const instrumentationContext = getInstrumentationContextsFromTableView(
            [rowKey],
            tableView
        )[0];

        const mailTableQuery = tableView?.tableQuery as MailFolderTableQuery;
        const isCategorySort = mailTableQuery?.sortBy?.sortColumn === 14;
        const isPrioritySort = mailTableQuery?.sortBy?.sortColumn === 16;

        // derived properties based on props/flights
        const densityModeString = getDensityModeCssClass(full, medium, compact);
        const isUnread = unreadCount > 0;
        const shouldShowCategories = categories?.length > 0 && !isCategorySort;
        const showAttachmentPreviews = !showCondensedView && showAttachmentPreview;
        const showPreviews =
            !shouldShowUnstackedReadingPane() ||
            (!isFirstLevelExpanded && !isSecondLevelExpanded && !isLoadingSecondLevelExpansion);

        const showCheckbox =
            isFeatureEnabled('tri-reselect-checkbox-behavior') ||
            showPersonaCheckbox(isSearchTable, tableView);
        const mailboxInfo = props.mailboxInfo ?? getMailboxInfo(tableView);
        const isItemView = tableView?.tableQuery.listViewType === 1;
        const { conditionalFormattingModifications: contextCfModifications } =
            useMailListItemContainerContext();
        const conditionalFormattingModifications = cfPreviewMods
            ? cfPreviewMods
            : contextCfModifications;

        const isDarkTheme = getIsDarkTheme();
        const shouldShowColumnHeaderStyles =
            isFeatureEnabled('tri-mlFocusSelect') && isFeatureEnabled('tri-mlColumnHeaderHover');
        const firstColumnStyles =
            isItemView || shouldShowTwisty
                ? firstColumnWithHeaders
                : firstColumnNoTwistyWithHeaders;

        const subjectClasses = classnames(
            isUnread ? subjectUnread : subject,
            useMessageListTextStyle('Major')
        );
        const isReadingPaneRight = isReadingPanePositionRight();
        const { senderColumnWidth, subjectColumnWidth, receivedColumnWidth } =
            getListViewColumnWidths();

        const firstColumnWidth = React.useMemo(() => {
            return isCfPreview
                ? {
                      width: `${DEFAULT_CF_PREVIEW_SENDER_COLUMN_WIDTH}%`,
                      maxWidth: `${DEFAULT_CF_PREVIEW_SENDER_COLUMN_WIDTH}%`,
                  }
                : {
                      width: senderColumnWidth,
                      maxWidth: senderColumnWidth,
                  };
        }, [senderColumnWidth, isCfPreview]);

        const secondColumnWidth = React.useMemo(() => {
            return isCfPreview
                ? {
                      width: `${DEFAULT_CF_PREVIEW_SUBJECT_COLUMN_WIDTH}%`,
                      maxWidth: `${DEFAULT_CF_PREVIEW_SUBJECT_COLUMN_WIDTH}%`,
                  }
                : {
                      width: subjectColumnWidth,
                      maxWidth: subjectColumnWidth,
                  };
        }, [subjectColumnWidth, isCfPreview]);

        const thirdColumnWidth = React.useMemo<CSSProperties>(() => {
            const thirdColumnMarginLeft = !shouldShowColumnHeaderStyles
                ? `${RECEIVED_COLUMN_LEFT_PADDING}px`
                : undefined;
            const thirdColumnPaddingLeft = shouldShowColumnHeaderStyles
                ? `${RECEIVED_COLUMN_LEFT_PADDING}px`
                : undefined;
            const thirdColumnPaddingRight = shouldShowColumnHeaderStyles
                ? isReadingPaneRight
                    ? `${MCL_RPRIGHT_LIST_ITEM_RIGHT_PADDING}px`
                    : `${MCL_LIST_ITEM_RIGHT_PADDING}px`
                : undefined;
            const thirdColumnBoxSizing = shouldShowColumnHeaderStyles
                ? 'border-box'
                : 'content-box';
            return {
                width: isCfPreview
                    ? `${DEFAULT_CF_PREVIEW_RECEIVED_COLUMN_WIDTH}%`
                    : `${receivedColumnWidth}px`,
                maxWidth: isCfPreview
                    ? `${DEFAULT_CF_PREVIEW_RECEIVED_COLUMN_WIDTH}%`
                    : `${receivedColumnWidth}px`,
                boxSizing: thirdColumnBoxSizing,
                paddingLeft: thirdColumnPaddingLeft,
                paddingRight: thirdColumnPaddingRight,
                marginLeft: thirdColumnMarginLeft,
            };
        }, [receivedColumnWidth, isReadingPaneRight, isCfPreview, shouldShowColumnHeaderStyles]);

        let senderFirstLineText = firstLineText;

        // In the Junk Email folder, we'll show the sender's email address if the
        // sender's name is not a valid email address so users can determine the validity
        // of the sender's email address without opening potentially dangerous emails.
        if (isInJunkFolder && !isValidEmailAddress(firstLineText)) {
            const lastSenderMailboxEmailAddress = lastSender?.Mailbox?.EmailAddress;

            // If the sender's email address is valid, we'll show the sender's email address.
            // Otherwise, we'll show the sender's name and a label indicating that the sender's
            // email address is not found.
            if (
                lastSenderMailboxEmailAddress &&
                isValidEmailAddress(lastSenderMailboxEmailAddress)
            ) {
                senderFirstLineText = `${firstLineText} <${lastSenderMailboxEmailAddress}>`;
            } else {
                senderFirstLineText = `${firstLineText} <${loc(senderEmailNotFoundLabel)}>`;
            }
        }

        const textStyle = React.useMemo(() => {
            return conditionalFormattingModifications &&
                conditionalFormattingModifications.length > 0
                ? getConditionalFormattingStyles(
                      ConditionalFormattingSurface.Text,
                      conditionalFormattingModifications,
                      isDarkTheme
                  )
                : undefined;
        }, [conditionalFormattingModifications, isDarkTheme]);

        const onPreviewClick = React.useCallback(
            (evt: React.MouseEvent<unknown> | React.TouchEvent<unknown>) => {
                onMailListItemClickHandler(
                    evt,
                    MailListItemSelectionSource.MailListItemRichPreview,
                    rowKey,
                    tableViewId
                );
            },
            [rowKey, tableViewId]
        );

        const folderTagComponent = (
            <FolderTag
                folderId={parentFolderId}
                parentFolderDisplayName={parentFolderDisplayName}
                isPreviewTextOn={showPreviewText}
                isSingleLine={true}
                mailboxGuids={mailboxGuids}
                shouldBeInTabOrder={isSelected}
            />
        );

        const timestampComponent = returnTime ? (
            <MailListItemTimestamp
                isPinned={isPinned}
                isSingleLine={true}
                isSnoozed={true}
                returnTime={returnTime ?? ''}
                supportsPinning={supportsPinning}
                isCfPreview={isCfPreview}
                conditionalFormattingModifications={conditionalFormattingModifications}
                isDarkTheme={isDarkTheme}
            />
        ) : (
            <MailListItemTimestamp
                isPinned={isPinned}
                isSingleLine={true}
                isUnread={isUnread}
                lastDeliveryTimestamp={lastDeliveryTimestamp}
                returnTime={returnTime ?? ''}
                supportsPinning={supportsPinning}
                isCfPreview={isCfPreview}
                conditionalFormattingModifications={conditionalFormattingModifications}
                isDarkTheme={isDarkTheme}
            />
        );

        const renderIconBarComponent = (
            neverShowHoverIcons: boolean,
            neverShowPropertyIcons: boolean
        ) => {
            return (
                <MailListItemIconBar
                    canArchive={canArchive}
                    canDelete={canDelete}
                    canPin={canPin}
                    effectiveMentioned={effectiveMentioned}
                    hasAttachment={hasAttachment}
                    hasSharepointLink={hasSharepointLink}
                    importance={importance}
                    isComplete={isComplete}
                    isFlagged={isFlagged}
                    isInArchiveFolder={isInArchiveFolder}
                    isInNotesFolder={isInNotesFolder}
                    isInOutboxFolder={isInOutboxFolder}
                    isPinned={isPinned}
                    isRowExpanded={isFirstLevelExpanded || isSecondLevelExpanded}
                    isSingleLine={true}
                    isSnoozed={isSnoozed}
                    isTaggedForBigScreen={isTaggedForBigScreen}
                    itemClassIcon={itemClassIcon}
                    lastDeliveryTimestamp={lastDeliveryTimestamp}
                    neverShowHoverIcons={neverShowHoverIcons || !supportsHoverIcons}
                    supportsHoverIcons={supportsHoverIcons}
                    neverShowPropertyIcons={neverShowPropertyIcons}
                    rowKey={rowKey}
                    smimeType={smimeType}
                    supportsFlagging={supportsFlagging}
                    supportsMove={supportsMove}
                    supportsPinning={supportsPinning}
                    tableViewId={tableViewId}
                    unreadCount={unreadCount}
                    isReminderSet={isReminderSet}
                />
            );
        };

        const rightSideIconBar = renderIconBarComponent(
            false /* neverShowHoverIcons */,
            true /* neverShowPropertyIcons */
        );

        const unreadBar = React.useMemo(() => {
            return (
                <MailListItemUnreadBar
                    isExpanded={isFirstLevelExpanded || isSecondLevelExpanded}
                    isUnread={isUnread}
                    rowKey={rowKey}
                    tableViewId={tableViewId}
                    conditionalFormattingModifications={conditionalFormattingModifications}
                    isDarkTheme={isDarkTheme}
                />
            );
        }, [
            isFirstLevelExpanded,
            isSecondLevelExpanded,
            isUnread,
            rowKey,
            tableViewId,
            conditionalFormattingModifications,
            isDarkTheme,
        ]);

        return (
            <>
                {unreadBar}
                <div
                    className={singleLineViewContainer}
                    style={isCfPreview ? cfPreviewBackgroundColorStyle : undefined}
                >
                    <div className={classnames(densityModeString, firstRowBase)}>
                        <div
                            className={classnames(
                                firstColumnStyles,
                                isItemView ? itemView : conversationView,
                                densityModeString,
                                shouldShowColumnHeaderStyles
                                    ? standardizedFirstColumn
                                    : standardizedFirstColumnDep
                            )}
                            style={firstColumnWidth}
                        >
                            {shouldShowTwisty && (
                                <MailListItemTwistyButton
                                    densityMode={getDensityModeString()}
                                    isDarkTheme={isDarkTheme}
                                    isFirstLevelExpanded={isFirstLevelExpanded}
                                    renderMinimum={renderMinimum}
                                    isSecondLevelExpanded={isSecondLevelExpanded}
                                    isLoadingSecondLevelExpansion={isLoadingSecondLevelExpansion}
                                    isSelected={isSelected}
                                    isSingleLine={true}
                                    isUnread={unreadCount > 0}
                                    rowKey={rowKey}
                                    showCondensedView={showCondensedView}
                                    tableViewId={tableViewId}
                                />
                            )}
                            {showCheckbox && (
                                <PersonaCheckbox
                                    tableViewId={tableViewId}
                                    isSelected={isSelected}
                                    lastSender={lastSender}
                                    rowKey={rowKey}
                                    lastDeliveryTimestamp={lastDeliveryTimestamp}
                                    isUnauthenticatedSender={isUnauthenticatedSender}
                                    isCFPreview={isCfPreview}
                                />
                            )}

                            <MailListItemMarker
                                shouldShowExternalLabel={shouldShowExternalLabel}
                                isUnauthenticatedSender={isUnauthenticatedSender}
                                isSelected={isSelected}
                                bulkCategory={bulkCategory}
                            />
                            <MailListItemFirstLineText
                                highlightTerms={highlightTerms}
                                highlightAccuracy={tableView?.highlightAccuracy}
                                showDraftsIndicator={showDraftsIndicator}
                                isInOutboxFolder={isInOutboxFolder}
                                unreadCount={unreadCount}
                                text={senderFirstLineText}
                                tooltipText={firstLineTooltipText ?? ''}
                                isSingleLineView={true}
                                isScheduledSend={isScheduledSend}
                                textColor={textStyle}
                            />
                            {!isReadingPaneRight
                                ? renderIconBarComponent(
                                      false /* neverShowHoverIcons */,
                                      false /* neverShowPropertyIcons */
                                  )
                                : renderIconBarComponent(
                                      true /* neverShowHoverIcons */,
                                      false /* neverShowPropertyIcons */
                                  )}
                        </div>
                        <div
                            className={classnames(
                                secondColumn,
                                isItemView
                                    ? mailListitemStyles_itemView
                                    : mailListitemStyles_conversationView,
                                densityModeString,
                                shouldShowColumnHeaderStyles
                                    ? standardizedSecondColumn
                                    : standardizedSecondColumnDep
                            )}
                            style={secondColumnWidth}
                        >
                            <CopilotInboxPriorityIcon
                                copilotInboxScore={copilotInboxScore}
                                scenario={isPrioritySort ? 'PrioritySort' : 'ML'}
                                mailboxInfo={mailboxInfo}
                                isUnread={isUnread}
                            />
                            {shouldShowCategories && (
                                <CategoryWell
                                    key={rowId}
                                    categoryWellContainerClass={categoryWellSingleLine}
                                    categoryContainerClass={categoryContainerSingleLine}
                                    categories={categories}
                                    onCategoryClicked={onCategoryClicked}
                                    mailboxInfo={mailboxInfo}
                                    conversationId={conversationId}
                                />
                            )}
                            <div className={content}>
                                <div className={subjectClasses} style={textStyle}>
                                    <MailListItemLineText
                                        addOverflowStyles={true}
                                        highlightTerms={highlightTerms}
                                        text={secondLineText}
                                        tooltipText={secondLineTooltipText ?? ''}
                                        conditionalFormattingModifications={
                                            conditionalFormattingModifications
                                        }
                                        isDarkTheme={isDarkTheme}
                                    />
                                    <MailListItemUnreadCount
                                        unreadCount={unreadCount}
                                        conditionalFormattingModifications={
                                            conditionalFormattingModifications
                                        }
                                        isDarkTheme={isDarkTheme}
                                    />
                                </div>
                                {showPreviewText && (
                                    <div className={classnames(previewDisplaySingleLine)}>
                                        {showPreviews && (
                                            <MailListItemPreviewDisplay
                                                highlightTerms={highlightTerms}
                                                previewDisplay={thirdLineText}
                                                rowId={rowId}
                                                mailboxInfo={mailboxInfo}
                                                textStyle={textStyle}
                                            />
                                        )}
                                    </div>
                                )}
                            </div>
                            {!(isFirstLevelExpanded || isSecondLevelExpanded) &&
                                renderReactions({
                                    latestItemId,
                                    reactionsContainerClassName: reactionsContainer,
                                    rowId,
                                    tableViewId,
                                })}
                            {shouldShowRSVP && (
                                <MailListItemMeetingInfo
                                    instrumentationContext={instrumentationContext}
                                    conversationId={conversationId}
                                    isSelected={isSelected}
                                    isSingleLineView={true}
                                    latestItemId={latestItemId}
                                    listViewType={listViewType}
                                    tableViewId={tableViewId}
                                    rowKey={rowKey}
                                />
                            )}
                            {shouldSuggestUnsubscribe && (
                                <SuggestedUnsubscribe
                                    isSingleLineView={true}
                                    isSelected={isSelected}
                                    rowId={rowId}
                                    tableView={tableView}
                                />
                            )}
                            {showFolderTag && folderTagComponent}
                        </div>
                        {/* SLV THIRD COLUMN STARTS HERE */}
                        {
                            <div style={thirdColumnWidth} className={mclRPRightThirdColumn}>
                                {timestampComponent}
                                {isReadingPaneRight && rightSideIconBar}
                            </div>
                        }
                        {/* SLV THIRD COLUMN ENDS HERE */}
                    </div>
                    {showPreviews && showAttachmentPreviews && (
                        <MailListItemRichPreviewWell
                            isWideView={true}
                            onPreviewClick={onPreviewClick}
                            rowId={rowId}
                            tableViewId={tableViewId}
                        />
                    )}
                </div>
            </>
        );
    },
    'MailListItemSingleLineView'
);
export default defaultExport;
